import { useEffect, useMemo, useState } from "react";
import { Plus, FileText, FileSpreadsheet, Columns3 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { buildBudgetRows, budgetPayload, fieldPatch, subpostPatch } from "@/lib/budgetRows";
import { downloadXlsxFrom } from "@/components/HistoricalsMatrix";
import { BudgetMatrixTable } from "@/components/budget/BudgetMatrixTable";
import { BudgetDescriptionBox } from "@/components/budget/BudgetDescriptionBox";
import { NewBudgetDialog } from "@/components/budget/NewBudgetDialog";
import { downloadBudgetComparePdf } from "@/components/budget/budgetComparePdf";
import { BtwHint } from "@/components/btw/BtwHint";
import { BtwBlock } from "@/components/btw/BtwBlock";
import { btwContext, btwForRow } from "@/lib/btw";

// Begrotingsmatrix: zelfde rijen als Voorgaande jaren, links het resultaat van de laatste
// jaarrekening (alleen lezen), rechts de begrotingsjaren met direct bewerkbare cellen.
export default function BudgetMatrix({ clubId, club, historicals, subposts, userTier, onSubpostsChange, clubLocked = false }) {
  const t = useT();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [allHist, setAllHist] = useState(false);

  const load = () => {
    api.get(`/clubs/${clubId}/budgets`)
      .then(({ data }) => setBudgets(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clubId]);

  const histSorted = useMemo(() => [...(historicals || [])].sort((a, b) => a.year - b.year), [historicals]);
  const histCols = allHist ? histSorted : histSorted.slice(-1);
  const rows = useMemo(() => buildBudgetRows(t, subposts, [...histSorted, ...budgets]), [t, subposts, histSorted, budgets]);
  const btwCtx = useMemo(() => btwContext(club, subposts), [club, subposts]);

  const suggestedYear = useMemo(() => {
    const latestBudget = budgets.length ? Math.max(...budgets.map((b) => b.year)) : 0;
    const latestHist = histSorted.length ? histSorted[histSorted.length - 1].year : 0;
    return Math.max(latestBudget + 1, latestHist + 1, new Date().getFullYear() + 1);
  }, [budgets, histSorted]);

  const putBudget = async (b, patch) => {
    try {
      await api.put(`/budgets/${b.id}`, budgetPayload(b, patch));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    }
  };
  const lockedMsg = () => toast.error(t("Deze begroting is vergrendeld — ontgrendel eerst."));

  const toggleLock = async (b) => {
    try {
      await api.patch(`/budgets/${b.id}/lock`, { locked: !b.locked });
      toast.success(!b.locked ? t("Begroting vergrendeld — kan niet meer worden overschreven.") : t("Begroting ontgrendeld — je kunt weer wijzigingen doorvoeren."));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    }
  };
  const removeBudget = async (b) => {
    if (b.locked) return lockedMsg();
    if (!window.confirm(t("Verwijder begroting {y}?").replace("{y}", String(b.year)))) return;
    try {
      await api.delete(`/budgets/${b.id}`);
      toast.success(t("Begroting verwijderd"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    }
  };
  const addSubpost = async (cat, name) => {
    try {
      await api.post(`/clubs/${clubId}/subposts`, { category: cat, name });
      toast.success(t("Subpost toegevoegd — ook zichtbaar bij Voorgaande jaren."));
      onSubpostsChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    }
  };

  const exportExcel = () => downloadXlsxFrom(`/clubs/${clubId}/budgets/begroting.xlsx?hist=${allHist ? "all" : 1}`, "export", t, clubId);
  const exportPdf = async () => {
    if (!budgets.length) return toast.error(t("Geen begroting om te vergelijken."));
    try {
      await downloadBudgetComparePdf({ t, club, clubId, budgets, historicals: histSorted, subposts, userTier });
      toast.success(t("PDF gedownload"));
    } catch (e) {
      toast.error(t("PDF maken mislukt") + (e?.message ? `: ${e.message}` : ""));
    }
  };

  return (
    <div className="bg-card border-2 border-foreground p-6 heavy-shadow" data-testid="budget-matrix">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="stat-label">{t("Voor de penningmeester")}</div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Begrotingen")}</h2>
            <BtwHint club={club} clubId={clubId} testid="budget-btw-hint" />
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">
            {t("Dezelfde opbouw als Voorgaande jaren. Links ter vergelijking het resultaat van de laatste jaarrekening; rechts de begroting. Klik in een cel, typ het bedrag en druk op Enter — ook per subpost.")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {histSorted.length > 1 && (
            <button onClick={() => setAllHist((v) => !v)} data-testid="budget-hist-toggle"
              className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2">
              <Columns3 className="w-4 h-4" /> {allHist ? t("Alleen laatste jaar") : t("Meer jaren tonen ({n})").replace("{n}", String(histSorted.length))}
            </button>
          )}
          <button onClick={exportExcel} disabled={budgets.length === 0} data-testid="budget-excel-btn"
            className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-40">
            <FileSpreadsheet className="w-4 h-4" /> {t("Excel")}
          </button>
          <button onClick={exportPdf} disabled={budgets.length === 0} data-testid="budget-compare-pdf-btn"
            className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-40">
            <FileText className="w-4 h-4" /> {t("Vergelijkings-PDF")}
          </button>
          {!clubLocked && (
            <button onClick={() => setNewOpen(true)} data-testid="budget-new-btn"
              className="btn-brutal bg-primary text-primary-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> {t("Nieuwe begroting")}
            </button>
          )}
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground py-4">{t("Bezig met laden…")}</div>}

      {!loading && budgets.length === 0 && (
        <div className="border-2 border-dashed border-foreground/40 p-8 text-center bg-muted/20" data-testid="budget-empty">
          <div className="stat-label mb-2">{t("Nog geen begroting")}</div>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">
            {t("Maak je eerste begroting aan — je kunt starten vanaf de laatste jaarrekening met een inflatie-aanpassing.")}
          </p>
        </div>
      )}

      {!loading && budgets.length > 0 && (
        <BudgetMatrixTable
          t={t} rows={rows} histCols={histCols} budgetCols={budgets} clubLocked={clubLocked}
          onCommitField={(b, field, v) => (b.locked ? lockedMsg() : putBudget(b, fieldPatch(b, field, v)))}
          onCommitSubpost={(b, cat, name, v) => (b.locked ? lockedMsg() : putBudget(b, subpostPatch(b, cat, name, v)))}
          onToggleLock={toggleLock} onDelete={removeBudget} onAddSubpost={addSubpost}
        />
      )}
      {!loading && budgets.length > 0 && btwCtx && (
        <BtwBlock t={t} testid="budget-btw-block" regime={btwCtx.regime} columns={[
          ...histCols.map((h) => ({ key: `h${h.year}`, label: `${t("Resultaat")} ${h.year}`, block: btwForRow(h, btwCtx), muted: true })),
          ...budgets.map((b) => ({ key: `b${b.year}`, label: `${t("Begroting")} ${b.year}`, block: btwForRow(b, btwCtx) })),
        ]} />
      )}

      {budgets.length > 0 && (
        <div className="mt-6 grid gap-3" data-testid="budget-descriptions-list">
          <div className="stat-label">{t("Toelichting per begroting")}</div>
          {budgets.map((b) => <BudgetDescriptionBox key={b.id} budget={b} onSaved={load} />)}
        </div>
      )}

      <NewBudgetDialog open={newOpen} onOpenChange={setNewOpen} clubId={clubId} historicals={histSorted}
        budgets={budgets} suggestedYear={suggestedYear} onCreated={load} />
    </div>
  );
}
